import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import DialogContentText from '@mui/material/DialogContentText';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/Person';
import SellIcon from '@mui/icons-material/Sell';
import PageShell from '../components/PageShell';
import { useImagePreview } from '../components/ImagePreview';
import request from '../api';
import { formatDateTime } from '../dateUtils';

const PRODUCT_STATUS_LABELS = {
    draft: 'Borrador',
    ready: 'Lista',
    on_auction: 'En Subasta',
    ready_to_deliver: 'Por Entregar',
    delivered: 'Entregada',
};

const PRODUCT_STATUS_COLORS = {
    draft: 'default',
    ready: 'info',
    on_auction: 'success',
    ready_to_deliver: 'warning',
    delivered: 'default',
};

function AdminDashboard() {
    const { openPreview } = useImagePreview();
    const [token] = useState(localStorage.getItem('adminToken'));
    const [stores, setStores] = useState([]);
    const [error, setError] = useState('');
    const [customers, setCustomers] = useState([]);
    const [customersError, setCustomersError] = useState('');
    const [products, setProducts] = useState([]);
    const [productsError, setProductsError] = useState('');
    const [resetResult, setResetResult] = useState(null);
    const [codeResult, setCodeResult] = useState(null);
    const [customerMenu, setCustomerMenu] = useState({ anchorEl: null, customer: null });
    const [gemsDialog, setGemsDialog] = useState({ open: false, customer: null, value: '', error: '' });
    const [storeApplications, setStoreApplications] = useState([]);
    const [applicationsError, setApplicationsError] = useState('');
    const [approveDialog, setApproveDialog] = useState({ open: false, application: null, username: '', password: '', error: '' });
    const navigate = useNavigate();

    const openCustomerMenu = (event, customer) => {
        setCustomerMenu({ anchorEl: event.currentTarget, customer });
    };

    const closeCustomerMenu = () => {
        setCustomerMenu({ anchorEl: null, customer: null });
    };

    const openGemsDialog = (customer) => {
        setGemsDialog({ open: true, customer, value: String(customer.gems), error: '' });
    };

    const closeGemsDialog = () => {
        setGemsDialog({ open: false, customer: null, value: '', error: '' });
    };

    const submitGemsUpdate = async (e) => {
        e.preventDefault();
        const value = Number(gemsDialog.value);
        if (!Number.isFinite(value) || value < 0) {
            setGemsDialog((prev) => ({ ...prev, error: 'Ingresa un número válido mayor o igual a 0' }));
            return;
        }
        try {
            await request(`/admin/customers/${gemsDialog.customer.id}/gems`, {
                method: 'PATCH',
                token,
                body: { gems: value },
            });
            closeGemsDialog();
            loadCustomers();
        } catch (err) {
            setGemsDialog((prev) => ({ ...prev, error: err.message }));
        }
    };

    const loadStores = useCallback(() => {
        if (!token) return;
        request('/admin/stores', { token })
            .then(setStores)
            .catch((err) => setError(err.message));
    }, [token]);

    const loadCustomers = useCallback(() => {
        if (!token) return;
        request('/admin/customers', { token })
            .then(setCustomers)
            .catch((err) => setCustomersError(err.message));
    }, [token]);

    const loadProducts = useCallback(() => {
        if (!token) return;
        request('/admin/products', { token })
            .then(setProducts)
            .catch((err) => setProductsError(err.message));
    }, [token]);

    const loadStoreApplications = useCallback(() => {
        if (!token) return;
        request('/admin/store-applications', { token })
            .then(setStoreApplications)
            .catch((err) => setApplicationsError(err.message));
    }, [token]);

    useEffect(() => {
        if (!token) {
            navigate('/admin/login');
            return;
        }
        loadStores();
        loadCustomers();
        loadProducts();
        loadStoreApplications();
    }, [token, navigate, loadStores, loadCustomers, loadProducts, loadStoreApplications]);

    const resetCustomerPassword = async (customer) => {
        setCustomersError('');
        try {
            const result = await request(`/admin/customers/${customer.id}/reset-password`, {
                method: 'PATCH',
                token,
            });
            setResetResult({ username: customer.username, tempPassword: result.temp_password });
        } catch (err) {
            setCustomersError(err.message);
        }
    };

    const viewVerificationCode = (customer) => {
        setCodeResult({
            username: customer.username,
            code: customer.verification_code,
            expiresAt: customer.verification_code_expires_at,
        });
    };

    const toggleActive = async (store) => {
        try {
            await request(`/admin/stores/${store.id}/${store.is_active ? 'deactivate' : 'activate'}`, {
                method: 'PATCH',
                token,
            });
            loadStores();
        } catch (err) {
            setError(err.message);
        }
    };

    const toggleCustomerActive = async (customer) => {
        setCustomersError('');
        try {
            await request(`/admin/customers/${customer.id}/${customer.is_active ? 'deactivate' : 'activate'}`, {
                method: 'PATCH',
                token,
            });
            loadCustomers();
        } catch (err) {
            setCustomersError(err.message);
        }
    };

    const openApproveDialog = (application) => {
        setApproveDialog({ open: true, application, username: '', password: '', error: '' });
    };

    const closeApproveDialog = () => {
        setApproveDialog({ open: false, application: null, username: '', password: '', error: '' });
    };

    const submitApprove = async (e) => {
        e.preventDefault();
        if (!approveDialog.username || !approveDialog.password) {
            setApproveDialog((prev) => ({ ...prev, error: 'Usuario y contraseña son obligatorios' }));
            return;
        }
        try {
            await request(`/admin/store-applications/${approveDialog.application.id}/approve`, {
                method: 'POST',
                token,
                body: { username: approveDialog.username, password: approveDialog.password },
            });
            closeApproveDialog();
            loadStoreApplications();
            loadStores();
        } catch (err) {
            setApproveDialog((prev) => ({ ...prev, error: err.message }));
        }
    };

    const rejectApplication = async (application) => {
        setApplicationsError('');
        try {
            await request(`/admin/store-applications/${application.id}/reject`, {
                method: 'POST',
                token,
            });
            loadStoreApplications();
        } catch (err) {
            setApplicationsError(err.message);
        }
    };

    const logout = () => {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
    };

    const nav = (
        <>
            <Button component={RouterLink} to="/admin/stores/new" color="inherit">Nueva Tienda</Button>
            <Button component={RouterLink} to="/auctions" color="inherit">Ver Subastas</Button>
            <Button onClick={logout} variant="outlined" color="inherit">Cerrar Sesión</Button>
        </>
    );

    const panelSx = {
        flex: '1 1 0',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: { xs: 420, md: 560 },
        p: { xs: 1, sm: 2 },
    };
    const scrollListSx = { overflowY: 'auto', flex: 1 };

    return (
        <PageShell title="Panel de Administrador" nav={nav} maxWidth="lg">
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="stretch">
                <Paper sx={panelSx}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1, pb: 1 }}>
                        <Typography variant="h6">Tiendas</Typography>
                        <Chip size="small" label={stores.length} />
                    </Stack>
                    <List sx={scrollListSx}>
                        {stores.map((store, i) => (
                            <React.Fragment key={store.id}>
                                <ListItem
                                    sx={{
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        rowGap: 1.5,
                                    }}
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: 'rgba(15,23,42,0.06)' }}>
                                            <StorefrontIcon />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={store.name}
                                        secondary={store.username}
                                        sx={{ flexGrow: 1, minWidth: 140 }}
                                    />
                                    <Tooltip title={store.is_active ? 'Activa' : 'Inactiva'}>
                                        <Switch
                                            checked={store.is_active}
                                            onChange={() => toggleActive(store)}
                                            color="success"
                                            inputProps={{ 'aria-label': `${store.is_active ? 'Desactivar' : 'Activar'} ${store.name}` }}
                                        />
                                    </Tooltip>
                                </ListItem>
                                {i < stores.length - 1 && <Divider component="li" sx={{ borderColor: 'divider' }} />}
                            </React.Fragment>
                        ))}
                        {stores.length === 0 && (
                            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                                Aún no se han creado tiendas.
                            </Typography>
                        )}
                    </List>
                </Paper>

                <Paper sx={panelSx}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1, pb: 1 }}>
                        <Typography variant="h6">Usuarios</Typography>
                        <Chip size="small" label={customers.length} />
                    </Stack>
                    {customersError && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{customersError}</Alert>}
                    <List sx={scrollListSx}>
                        {customers.map((customer, i) => (
                            <React.Fragment key={customer.id}>
                                <ListItem
                                    sx={{
                                        flexWrap: 'nowrap',
                                        gap: 1,
                                    }}
                                >
                                    <ListItemAvatar sx={{ minWidth: 44 }}>
                                        <Avatar sx={{ bgcolor: 'rgba(15,23,42,0.06)' }}>
                                            <PersonIcon />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={customer.username}
                                        secondary={customer.email || 'Sin correo registrado'}
                                        primaryTypographyProps={{ noWrap: true }}
                                        secondaryTypographyProps={{ noWrap: true }}
                                        sx={{ flexGrow: 1, minWidth: 0 }}
                                    />
                                    {!customer.email_verified && (
                                        <Chip size="small" label="Sin verificar" color="warning" sx={{ flexShrink: 0 }} />
                                    )}
                                    <Tooltip title={customer.is_active ? 'Activo' : 'Inactivo'}>
                                        <Switch
                                            checked={customer.is_active}
                                            onChange={() => toggleCustomerActive(customer)}
                                            color="success"
                                            sx={{ flexShrink: 0 }}
                                            inputProps={{ 'aria-label': `${customer.is_active ? 'Desactivar' : 'Activar'} ${customer.username}` }}
                                        />
                                    </Tooltip>
                                    <IconButton
                                        size="small"
                                        edge="end"
                                        aria-label={`Más acciones para ${customer.username}`}
                                        onClick={(e) => openCustomerMenu(e, customer)}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                </ListItem>
                                {i < customers.length - 1 && <Divider component="li" sx={{ borderColor: 'divider' }} />}
                            </React.Fragment>
                        ))}
                        {customers.length === 0 && (
                            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                                Aún no hay clientes registrados.
                            </Typography>
                        )}
                    </List>
                </Paper>

                <Menu
                    anchorEl={customerMenu.anchorEl}
                    open={Boolean(customerMenu.anchorEl)}
                    onClose={closeCustomerMenu}
                >
                    <MenuItem
                        onClick={() => {
                            resetCustomerPassword(customerMenu.customer);
                            closeCustomerMenu();
                        }}
                    >
                        Restablecer Contraseña
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            openGemsDialog(customerMenu.customer);
                            closeCustomerMenu();
                        }}
                    >
                        Actualizar Gemas
                    </MenuItem>
                    {customerMenu.customer && !customerMenu.customer.email_verified && customerMenu.customer.verification_code && (
                        <MenuItem
                            onClick={() => {
                                viewVerificationCode(customerMenu.customer);
                                closeCustomerMenu();
                            }}
                        >
                            Ver Código
                        </MenuItem>
                    )}
                </Menu>

                <Paper sx={panelSx}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1, pb: 1 }}>
                        <Typography variant="h6">Publicaciones</Typography>
                        <Chip size="small" label={products.length} />
                    </Stack>
                    {productsError && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{productsError}</Alert>}
                    <List sx={scrollListSx}>
                        {products.map((product, i) => (
                            <React.Fragment key={product.id}>
                                <ListItem
                                    sx={{
                                        flexWrap: 'nowrap',
                                        gap: 1,
                                    }}
                                >
                                    <ListItemAvatar sx={{ minWidth: 44 }}>
                                        <Avatar
                                            variant="rounded"
                                            src={product.image_url || undefined}
                                            onClick={product.image_url ? () => openPreview(product.image_url, product.title) : undefined}
                                            sx={{ bgcolor: 'rgba(15,23,42,0.06)', cursor: product.image_url ? 'zoom-in' : 'default' }}
                                        >
                                            <SellIcon />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={product.title}
                                        secondary={product.store_name}
                                        primaryTypographyProps={{ noWrap: true }}
                                        secondaryTypographyProps={{ noWrap: true }}
                                        sx={{ flexGrow: 1, minWidth: 0 }}
                                    />
                                    <Chip
                                        size="small"
                                        label={PRODUCT_STATUS_LABELS[product.status] || product.status}
                                        color={PRODUCT_STATUS_COLORS[product.status] || 'default'}
                                        sx={{ flexShrink: 0 }}
                                    />
                                </ListItem>
                                {i < products.length - 1 && <Divider component="li" sx={{ borderColor: 'divider' }} />}
                            </React.Fragment>
                        ))}
                        {products.length === 0 && (
                            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                                Aún no hay publicaciones.
                            </Typography>
                        )}
                    </List>
                </Paper>
            </Stack>

            <Paper sx={{ mt: 3, p: { xs: 1, sm: 2 } }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1, pb: 1 }}>
                    <Typography variant="h6">Solicitudes de Tienda</Typography>
                    <Chip size="small" label={storeApplications.length} />
                </Stack>
                {applicationsError && <Alert severity="error" sx={{ mx: 2, mb: 1 }}>{applicationsError}</Alert>}
                <TableContainer sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Tienda</TableCell>
                                <TableCell>Contacto</TableCell>
                                <TableCell>Correo</TableCell>
                                <TableCell>WhatsApp</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {storeApplications.map((application) => (
                                <TableRow key={application.id}>
                                    <TableCell>{application.store_name}</TableCell>
                                    <TableCell>{application.contact_name}</TableCell>
                                    <TableCell>{application.email}</TableCell>
                                    <TableCell>{application.whatsapp}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                            <Chip
                                                size="small"
                                                label={
                                                    application.status === 'approved'
                                                        ? 'Aprobada'
                                                        : application.status === 'rejected'
                                                        ? 'Rechazada'
                                                        : 'Pendiente'
                                                }
                                                color={
                                                    application.status === 'approved'
                                                        ? 'success'
                                                        : application.status === 'rejected'
                                                        ? 'default'
                                                        : 'warning'
                                                }
                                            />
                                            {!application.email_verified && application.status === 'pending' && (
                                                <Chip size="small" label="Correo sin verificar" variant="outlined" />
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">
                                        {application.status === 'pending' && (
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    disabled={!application.email_verified}
                                                    onClick={() => openApproveDialog(application)}
                                                >
                                                    Aprobar
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    onClick={() => rejectApplication(application)}
                                                >
                                                    Rechazar
                                                </Button>
                                            </Stack>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {storeApplications.length === 0 && (
                    <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        Aún no hay solicitudes de tienda.
                    </Typography>
                )}
            </Paper>

            <Dialog open={approveDialog.open} onClose={closeApproveDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Aprobar Tienda</DialogTitle>
                <Box component="form" onSubmit={submitApprove}>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>
                            Asigna el usuario y contraseña de acceso para{' '}
                            <strong>{approveDialog.application?.store_name}</strong>.
                        </DialogContentText>
                        <Stack spacing={2}>
                            <TextField
                                label="Usuario"
                                value={approveDialog.username}
                                onChange={(e) => setApproveDialog((prev) => ({ ...prev, username: e.target.value, error: '' }))}
                                fullWidth
                                autoFocus
                            />
                            <TextField
                                label="Contraseña"
                                type="password"
                                value={approveDialog.password}
                                onChange={(e) => setApproveDialog((prev) => ({ ...prev, password: e.target.value, error: '' }))}
                                fullWidth
                            />
                        </Stack>
                        {approveDialog.error && <Alert severity="error" sx={{ mt: 2 }}>{approveDialog.error}</Alert>}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeApproveDialog}>Cancelar</Button>
                        <Button type="submit" variant="contained">Aprobar</Button>
                    </DialogActions>
                </Box>
            </Dialog>

            <Dialog open={gemsDialog.open} onClose={closeGemsDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Actualizar Gemas</DialogTitle>
                <Box component="form" onSubmit={submitGemsUpdate}>
                    <DialogContent>
                        <Typography sx={{ mb: 2 }}>
                            Establece el nuevo saldo de gemas de <strong>{gemsDialog.customer?.username}</strong>.
                            Usa esto para corregir problemas con una compra de gemas.
                        </Typography>
                        <TextField
                            label="Gemas"
                            type="number"
                            value={gemsDialog.value}
                            onChange={(e) => setGemsDialog((prev) => ({ ...prev, value: e.target.value, error: '' }))}
                            fullWidth
                            autoFocus
                            inputProps={{ min: 0, step: '0.01' }}
                        />
                        {gemsDialog.error && <Alert severity="error" sx={{ mt: 2 }}>{gemsDialog.error}</Alert>}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeGemsDialog}>Cancelar</Button>
                        <Button type="submit" variant="contained">Guardar</Button>
                    </DialogActions>
                </Box>
            </Dialog>

            <Dialog open={Boolean(resetResult)} onClose={() => setResetResult(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Contraseña Restablecida</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Comparte esta contraseña temporal con <strong>{resetResult?.username}</strong> por un
                        canal seguro (llamada, WhatsApp, en persona). No volverá a mostrarse.
                    </Typography>
                    <TextField
                        label="Contraseña Temporal"
                        value={resetResult?.tempPassword || ''}
                        fullWidth
                        InputProps={{ readOnly: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetResult(null)}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(codeResult)} onClose={() => setCodeResult(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Código de Verificación</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Comparte este código con <strong>{codeResult?.username}</strong> para que pueda
                        verificar su cuenta manualmente si no recibió el correo.
                    </Typography>
                    <TextField
                        label="Código"
                        value={codeResult?.code || ''}
                        fullWidth
                        InputProps={{ readOnly: true }}
                    />
                    {codeResult?.expiresAt && new Date(codeResult.expiresAt) < new Date() ? (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Este código ya expiró ({formatDateTime(codeResult.expiresAt)}). Pídele al cliente
                            que use "Reenviar código" para obtener uno nuevo.
                        </Alert>
                    ) : (
                        codeResult?.expiresAt && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                Expira: {formatDateTime(codeResult.expiresAt)}
                            </Typography>
                        )
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCodeResult(null)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </PageShell>
    );
}

export default AdminDashboard;
